import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { createTechnologyProductFamily, ApiError } from '../../lib/apiClient';
import type { CreateTechnologyProductFamilyInput } from '../../lib/apiClient';
import { LoadingSpinner, RichTextEditor, isDeltaEmpty } from '../../components/shared';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';

export const Route = createFileRoute('/technology-families/new')({
  component: NewTechnologyProductFamilyPage,
});

export { NewTechnologyProductFamilyPage };

function NewTechnologyProductFamilyPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateTechnologyProductFamilyInput = {
        name: name.trim(),
        description: isDeltaEmpty(description) ? undefined : description,
      };
      await createTechnologyProductFamily(input);
      await apolloClient.refetchQueries({ include: ['ListTechnologyProductFamilies'] });
      void navigate({ to: '/technology-families' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create product family');
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) return <LoadingSpinner centered message="Creating product family..." />;

  return (
    <div className="simple-entity-form-page">
      <div className="simple-entity-form-page__back-link">
        <Link to="/technology-families" className="simple-entity-form-page__back-link-inner">
          <IconElement
            name="arrow_back"
            aria-hidden="true"
            className="simple-entity-form-page__back-icon"
          />
          <span className="forge-typography--body2">Back to Product Families</span>
        </Link>
      </div>

      <h1 className="forge-typography--headline4 simple-entity-form-page__title">
        Create Product Family
      </h1>

      {error && (
        <p role="alert" className="simple-entity-form-page__error">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="simple-entity-form-page__form">
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Name <span className="simple-entity-form-page__required">*</span>
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Databases, Compute, Messaging"
            required
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Description
          </label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Brief description of this product family"
          />
        </div>
        <div className="simple-entity-form-page__actions">
          <Link to="/technology-families">
            <ForgeButton type="button" variant="outlined">
              Cancel
            </ForgeButton>
          </Link>
          <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
            Create Family
          </ForgeButton>
        </div>
      </form>
    </div>
  );
}
